/**
 * Help Assistant Module
 * File: help-assistant.js
 * Location: Theme/src/js/custom/common/help-assistant.js
 *
 * Drives the floating FAQ helper rendered by Views/Shared/Components/Partials/_HelpAssistant.cshtml.
 * It is a guided conversation, not a free-text chat: the visitor picks a question and the
 * matching answer is shown as a reply.
 *
 * Security Compliance: VAPT M25 1806 Reserve Bank of Fiji
 * - External script file, no inline handlers (CSP Compliant - CRITICAL-003)
 * - No innerHTML / eval: replies are cloned from server-rendered markup, and any text
 *   the script adds itself goes in through textContent (XSS Prevention - MEDIUM-004)
 *
 * JavaScript Class Declaration Style:
 * - Function-type classes as per C# .NET Core Clean Architecture Guide
 * - Expose public methods through return statement
 * - Use const and let (no var)
 */

"use strict";

/**
 * Help Assistant Module
 * @returns {Object} Public API methods
 */
const HelpAssistant = (function () {
    // ============================================
    // PRIVATE CONSTANTS
    // ============================================

    const SELECTORS = Object.freeze({
        root: '[data-help-assistant]',
        launcher: '[data-ha-launcher]',
        panel: '[data-ha-panel]',
        close: '[data-ha-close]',
        body: '[data-ha-body]',
        log: '[data-ha-log]',
        choices: '[data-ha-choices]',
        content: '[data-ha-content]',
        greeting: '[data-ha-greeting]',
        followup: '[data-ha-followup]',
        topic: '[data-ha-topic]',
        question: '[data-ha-question]',
        answer: '[data-ha-answer]',
        contact: '[data-ha-contact]',
        copy: '[data-ha-copy]',
        deferredImage: 'img[data-ha-src]'
    });

    const CSS_CLASSES = Object.freeze({
        open: 'help-assistant--open',
        message: 'help-assistant__message',
        messageBot: 'help-assistant__message--bot',
        messageUser: 'help-assistant__message--user',
        typing: 'help-assistant__typing',
        label: 'help-assistant__label',
        chip: 'help-assistant__chip',
        chipContact: 'help-assistant__chip--contact',
        chipBack: 'help-assistant__chip--back',
        topic: 'help-assistant__topic',
        topicIcon: 'help-assistant__topic-icon',
        topicText: 'help-assistant__topic-text',
        topicTitle: 'help-assistant__topic-title',
        topicCount: 'help-assistant__topic-count'
    });

    const LABELS = Object.freeze({
        open: 'Open help',
        close: 'Close help',
        common: 'Common questions',
        topics: 'Browse by topic',
        allTopics: 'All topics',
        contactFallback: 'Contact us',
        copied: 'Copied',
        typing: 'Help assistant is typing'
    });

    const TIMING = Object.freeze({
        typingDelay: 650,     // milliseconds the typing indicator shows before a reply
        copiedReset: 2000     // milliseconds the "Copied" confirmation stays on the button
    });

    const SCROLL_PADDING = 12;   // pixels kept above a message scrolled into view

    // ============================================
    // PRIVATE STATE
    // ============================================

    let isInitialized = false;
    let hasGreeted = false;
    let isReplying = false;
    let currentTopicId = null;
    let topics = [];
    const answeredIds = new Set();
    const elements = {};

    // ============================================
    // CONTENT
    // ============================================

    /**
     * Reads the topics and questions out of the server-rendered content block
     * @returns {Array} Topics, each with its questions
     */
    const readTopics = function () {
        return Array.from(elements.content.querySelectorAll(SELECTORS.topic)).map(function (topicEl) {
            return {
                id: topicEl.getAttribute('data-ha-topic'),
                title: topicEl.getAttribute('data-ha-title') || '',
                intro: topicEl.getAttribute('data-ha-intro') || '',
                icon: topicEl.getAttribute('data-ha-icon') || '',
                questions: Array.from(topicEl.querySelectorAll(SELECTORS.question)).map(function (questionEl) {
                    const heading = questionEl.querySelector('h3');
                    const text = heading ? heading.textContent.trim() : '';
                    const commonOrder = parseInt(questionEl.getAttribute('data-ha-common'), 10);

                    return {
                        id: questionEl.getAttribute('data-ha-question'),
                        text: text,
                        shortText: questionEl.getAttribute('data-ha-short') || text,
                        commonOrder: isNaN(commonOrder) ? null : commonOrder,
                        answerEl: questionEl.querySelector(SELECTORS.answer)
                    };
                })
            };
        });
    };

    const findTopic = function (topicId) {
        return topics.find(function (topic) { return topic.id === topicId; }) || null;
    };

    const unanswered = function (questions) {
        return questions.filter(function (question) { return !answeredIds.has(question.id); });
    };

    const commonQuestions = function () {
        const all = topics.reduce(function (list, topic) { return list.concat(topic.questions); }, []);

        return unanswered(all)
            .filter(function (question) { return question.commonOrder !== null; })
            .sort(function (a, b) { return a.commonOrder - b.commonOrder; });
    };

    // ============================================
    // CONVERSATION LOG
    // ============================================

    const prefersReducedMotion = function () {
        return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    };

    const scrollTo = function (element) {
        if (!element) return;
        // offsetTop is measured from the scroll container, which is position: relative
        const top = Math.max(element.offsetTop - SCROLL_PADDING, 0);
        elements.body.scrollTo({ top: top, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    };

    const createMessage = function (modifierClass) {
        const message = document.createElement('div');
        message.className = CSS_CLASSES.message + ' ' + modifierClass;
        return message;
    };

    const addUserMessage = function (text) {
        const message = createMessage(CSS_CLASSES.messageUser);
        message.textContent = text;
        elements.log.appendChild(message);
        return message;
    };

    const addBotText = function (text) {
        const message = createMessage(CSS_CLASSES.messageBot);
        message.textContent = text;
        elements.log.appendChild(message);
        return message;
    };

    /**
     * Adds a reply by cloning server-rendered markup, so no HTML is built from strings
     * @param {Element} sourceEl - Element whose children form the reply
     */
    const addBotClone = function (sourceEl) {
        const message = createMessage(CSS_CLASSES.messageBot);
        Array.from(sourceEl.childNodes).forEach(function (node) {
            message.appendChild(node.cloneNode(true));
        });
        // Figures are rendered without a src so they are not downloaded until shown
        message.querySelectorAll(SELECTORS.deferredImage).forEach(function (image) {
            image.src = image.getAttribute('data-ha-src');
        });
        elements.log.appendChild(message);
        return message;
    };

    const addTypingIndicator = function () {
        const message = createMessage(CSS_CLASSES.messageBot + ' ' + CSS_CLASSES.typing);
        message.setAttribute('aria-hidden', 'true');
        message.setAttribute('title', LABELS.typing);
        for (let i = 0; i < 3; i++) {
            message.appendChild(document.createElement('span'));
        }
        elements.log.appendChild(message);
        return message;
    };

    /**
     * Shows the visitor's pick, a short typing pause, then the reply
     * @param {string} userText - What the visitor picked
     * @param {Function} addReply - Appends the reply message(s) to the log
     * @param {Function} renderNext - Renders the choices offered after the reply
     */
    const reply = function (userText, addReply, renderNext) {
        if (isReplying) return;
        isReplying = true;

        clearChoices();
        const userMessage = addUserMessage(userText);
        const typing = addTypingIndicator();
        scrollTo(userMessage);

        window.setTimeout(function () {
            typing.remove();
            addReply();
            renderNext();
            isReplying = false;
            scrollTo(userMessage);
            focusFirstChoice();
        }, prefersReducedMotion() ? 0 : TIMING.typingDelay);
    };

    // ============================================
    // CHOICES
    // ============================================

    const clearChoices = function () {
        while (elements.choices.firstChild) {
            elements.choices.removeChild(elements.choices.firstChild);
        }
    };

    const focusFirstChoice = function () {
        const first = elements.choices.querySelector('button');
        if (first) first.focus({ preventScroll: true });
    };

    const addLabel = function (text) {
        const label = document.createElement('p');
        label.className = CSS_CLASSES.label;
        label.textContent = text;
        elements.choices.appendChild(label);
    };

    const addChip = function (text, onSelect, modifierClass) {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = CSS_CLASSES.chip + (modifierClass ? ' ' + modifierClass : '');
        chip.textContent = text;
        chip.addEventListener('click', onSelect);
        elements.choices.appendChild(chip);
    };

    const addTopicButton = function (topic) {
        const remaining = unanswered(topic.questions).length;
        if (remaining === 0) return;

        const button = document.createElement('button');
        button.type = 'button';
        button.className = CSS_CLASSES.topic;

        const icon = document.createElement('i');
        icon.className = CSS_CLASSES.topicIcon + ' ki-outline ' + topic.icon;
        icon.setAttribute('aria-hidden', 'true');

        const text = document.createElement('span');
        text.className = CSS_CLASSES.topicText;

        const title = document.createElement('span');
        title.className = CSS_CLASSES.topicTitle;
        title.textContent = topic.title;

        const count = document.createElement('span');
        count.className = CSS_CLASSES.topicCount;
        count.textContent = remaining + (remaining === 1 ? ' question' : ' questions');

        text.appendChild(title);
        text.appendChild(count);
        button.appendChild(icon);
        button.appendChild(text);
        button.addEventListener('click', function () { selectTopic(topic); });
        elements.choices.appendChild(button);
    };

    const addContactChip = function () {
        if (!elements.contact) return;
        const label = elements.contact.getAttribute('data-ha-label') || LABELS.contactFallback;
        addChip(label, function () { selectContact(label); }, CSS_CLASSES.chipContact);
    };

    const renderHome = function () {
        currentTopicId = null;
        clearChoices();

        const common = commonQuestions();
        if (common.length > 0) {
            addLabel(LABELS.common);
            common.forEach(function (question) {
                addChip(question.shortText, function () { selectQuestion(question); });
            });
        }

        addLabel(LABELS.topics);
        topics.forEach(addTopicButton);
        addContactChip();
    };

    const renderTopic = function (topic) {
        currentTopicId = topic.id;
        clearChoices();

        unanswered(topic.questions).forEach(function (question) {
            addChip(question.text, function () { selectQuestion(question); });
        });

        addChip(LABELS.allTopics, function () {
            renderHome();
            scrollTo(elements.choices);
            focusFirstChoice();
        }, CSS_CLASSES.chipBack);
        addContactChip();
    };

    /**
     * Renders what to offer after a reply: the rest of the open topic, else the home list
     */
    const renderAfterReply = function () {
        const topic = findTopic(currentTopicId);
        if (topic && unanswered(topic.questions).length > 0) {
            renderTopic(topic);
        } else {
            renderHome();
        }
    };

    // ============================================
    // SELECTION HANDLERS
    // ============================================

    const selectTopic = function (topic) {
        reply(topic.title,
            function () { addBotText(topic.intro); },
            function () { renderTopic(topic); });
    };

    const selectQuestion = function (question) {
        reply(question.text,
            function () {
                answeredIds.add(question.id);
                if (question.answerEl) addBotClone(question.answerEl);
                if (elements.followup) addBotText(elements.followup.textContent.trim());
            },
            renderAfterReply);
    };

    const selectContact = function (label) {
        reply(label,
            function () { addBotClone(elements.contact); },
            renderAfterReply);
    };

    /**
     * Copies the enquiry address; the log is delegated because replies are clones
     * @param {Event} event - Click event from the conversation log
     */
    const handleLogClick = function (event) {
        const button = event.target.closest(SELECTORS.copy);
        if (!button || !navigator.clipboard) return;

        const originalText = button.textContent;
        navigator.clipboard.writeText(button.getAttribute('data-ha-copy')).then(function () {
            button.textContent = LABELS.copied;
            window.setTimeout(function () { button.textContent = originalText; }, TIMING.copiedReset);
        }).catch(function () {
            // Clipboard blocked by the browser: the address is still shown as a mailto link
        });
    };

    // ============================================
    // OPEN / CLOSE
    // ============================================

    const isOpen = function () {
        return !elements.panel.hidden;
    };

    const open = function () {
        if (isOpen()) return;

        elements.panel.hidden = false;
        elements.root.classList.add(CSS_CLASSES.open);
        elements.launcher.setAttribute('aria-expanded', 'true');
        elements.launcher.setAttribute('aria-label', LABELS.close);

        if (!hasGreeted) {
            hasGreeted = true;
            if (elements.greeting) addBotText(elements.greeting.textContent.trim());
            renderHome();
        }
        focusFirstChoice();
    };

    const close = function () {
        if (!isOpen()) return;

        elements.panel.hidden = true;
        elements.root.classList.remove(CSS_CLASSES.open);
        elements.launcher.setAttribute('aria-expanded', 'false');
        elements.launcher.setAttribute('aria-label', LABELS.open);
        elements.launcher.focus();
    };

    const handleKeydown = function (event) {
        if (event.key === 'Escape' && isOpen()) {
            event.stopPropagation();
            close();
        }
    };

    // ============================================
    // PUBLIC API
    // ============================================

    const init = function () {
        if (isInitialized) return;

        elements.root = document.querySelector(SELECTORS.root);
        if (!elements.root) return;

        ['launcher', 'panel', 'close', 'body', 'log', 'choices', 'content'].forEach(function (key) {
            elements[key] = elements.root.querySelector(SELECTORS[key]);
        });
        if (!elements.launcher || !elements.panel || !elements.content) return;

        elements.greeting = elements.content.querySelector(SELECTORS.greeting);
        elements.followup = elements.content.querySelector(SELECTORS.followup);
        elements.contact = elements.content.querySelector(SELECTORS.contact);
        topics = readTopics();

        elements.launcher.addEventListener('click', function () {
            if (isOpen()) { close(); } else { open(); }
        });
        elements.close.addEventListener('click', close);
        elements.log.addEventListener('click', handleLogClick);
        elements.root.addEventListener('keydown', handleKeydown);

        isInitialized = true;
    };

    return {
        init: init,
        open: open,
        close: close
    };
})();

document.addEventListener('DOMContentLoaded', function () {
    HelpAssistant.init();
});
